import * as vErrorMessages from 'Constants/validationErrors'

/**
 * @module Utils/validate
 * @requires Constants/validationErrors
 * */

/**
 * Парсер строковых шаблонов
 * @function
 * @param {String} string - Строковой шаблон
 * @param {...String} rest - Подставляемые значения
 * @return {String}
 * */
export function stringTemplateParser(string, ...rest) {
  return rest.reduce(
    (previousValue, currentValue) => previousValue.replace(/{[\S]*}/, currentValue),
    string
  )
}

/**
 * Валидация email
 * @function
 * @param {String} val
 * @return {Boolean}
 * */
export const email = val => /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(val)

/**
 * Валидация номера телефона в фоомате E164
 * @function
 * @param {String} val
 * @return {Boolean}
 * */
export const phone = val => /^\+?[1-9]\d{10}$/.test(val)

/**
 * Валидация обязательного поля
 * @function
 * @param {String} val
 * @return {Boolean}
 * */
export const required = val => {
  if (typeof val === 'string') return !!(val.trim())
  return !!val
}

/**
 * Валидация кирилица и пробелы
 * @function
 * @param {String} val
 * @return {Boolean}
 * */
export const cyrillicAndSpace = val => /^[а-яА-Я ]*$/.test(val)

/**
 * Валидация только цифры
 * @function
 * @param {String} val
 * @return {Boolean}
 * */
export const digit = val => /^\d+$/.test(val)

/**
 * Валидация время вида ##:##
 * @function
 * @param {String} val
 * @return {Boolean}
 * */
export const time = val => /^([0-1]?\d|2[0-3]):[0-5]\d$/.test(val)

/**
 * Валидация на количество слов в строке
 * определяется пробелами между символами
 * @function
 * @param {String} val
 * @param {Number} count
 * @return {Boolean}
 * */
export const wordsCount = (val, count) => {
  const spaces = val.trim().match(/\s+/g)
  return spaces?.length === count - 1
}

/**
 * Валидация на количество символов в строке
 * @function
 * @param {String} val
 * @param {Number} min=0
 * @param {Number} [max]
 * @return {Boolean}
 * */
export const stringLengthRange = (val, min = 0, max) => (
  val.length >= min && (!max || val.length <= max)
)

/**
 * Валидация на минимальное и максимальное зачение числа
 * @function
 * @param {Number} val
 * @param {Number} min=0
 * @param {Number} [max]
 * @return {Boolean}
 * */
export const numberRange = (val, min = 0, max) => (
  val >= min && (!max || val <= max)
)

/**
 * Правило валидации в стиле Quasar
 * @typedef QuasarRule
 * @function
 * @param {String} val
 * @return {Boolean|String} - true | Сообщение об ошибке
 * */

/**
 * Правило валидации для email
 * @type QuasarRule
 * */
export const emailRule = val => !val || email(val) || vErrorMessages.EMAIL

/**
 * Правило валидации обязательного поля
 * @type QuasarRule
 * */
export const requiredRule = val => required(val) || vErrorMessages.REQUIRED

/**
 * Правило валидации для номера телефона
 * @type QuasarRule
 * */
export const phoneRule = val => !val || phone(val) || vErrorMessages.PHONE

/**
 * Правило валидации в строке только цифры
 * @type QuasarRule
 * */
export const digitRule = val => !val || digit(val) || vErrorMessages.DIGIT

/**
 * Правило валидации в строке время вида ##:##
 * @type QuasarRule
 * */
export const timeRule = val => !val || digit(val) || vErrorMessages.TIME

/**
 * Фабрика возвращающая Правило валидации на количество символов в строке
 * в стиле Quasar
 * @param {Number} min=0
 * @param {Number} [max]
 * @return {QuasarRule}
 * */
export function stringLengthRangeRuleFabric(min = 0, max) {
  const {
    STRING_RANGE_MIN_MAX, STRING_RANGE_MIN, STRING_RANGE_MAX, STRING_RANGE_EXACT
  } = vErrorMessages
  return val => (
    !val
    || stringLengthRange(val, min, max)
    || (max && min && min === max && stringTemplateParser(STRING_RANGE_EXACT, min))
    || (max && min && stringTemplateParser(STRING_RANGE_MIN_MAX, min, max))
    || (min && stringTemplateParser(STRING_RANGE_MIN, min))
    || (max && stringTemplateParser(STRING_RANGE_MAX, max))
  )
}

/**
 * Фабрика возвращающая Правило валидации на минимальное и максимальное зачение числа
 * в стиле Quasar
 * @param {Number} min=0
 * @param {Number} [max]
 * @return {QuasarRule}
 * */
export function numberRangeRuleFabric(min = 0, max) {
  const {
    NUMBER_RANGE_MIN_MAX, NUMBER_RANGE_MIN, NUMBER_RANGE_MAX, NUMBER_RANGE_EXACT
  } = vErrorMessages
  return val => (
    !val
    || numberRange(val, min, max)
    || (max && min && min === max && stringTemplateParser(NUMBER_RANGE_EXACT, min))
    || (max && min && stringTemplateParser(NUMBER_RANGE_MIN_MAX, min, max))
    || (min && stringTemplateParser(NUMBER_RANGE_MIN, min))
    || (max && stringTemplateParser(NUMBER_RANGE_MAX, max))
  )
}

/**
 * Фабрика возвращающая Правило валидации на количество символов в строке
 * в стиле Quasar
 * @param {Number} wordsCountArg
 * @return {QuasarRule} - Правило валидации на количество символов в строке
 * */
export function wordsCountRuleFabric(wordsCountArg) {
  const { WORDS_COUNT } = vErrorMessages
  return val => (
    !val
    || wordsCount(val, wordsCountArg)
    || stringTemplateParser(WORDS_COUNT, wordsCountArg)
  )
}
